import { useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from '@core/hooks/redux-hooks';
import * as projectActions from '@core/store/project-slice';
import { OpmInputTable } from '@app/modules/projects/inputs/opm-inputs-2/OpmInputTable';
import { cloneToMutableData, enumKeyByValue } from '@app/shared/helpers';
import { formConfigBase } from '@app/shared/constants/form-config-base';
import useIsReadOnly from '@core/hooks/customUseIsReadOnly';
import {
  CalcMethod,
  DividendSetId,
  EventKey,
  OpmInputDataSource,
  OpmSimulatedValue,
  StaticCaseSelectionOptions,
} from '@app/shared/models/contracts/enums/shared-enums';
import { useOpmInputsFromPwerm } from './utils/useOpmInputsFromPwerm';
import { OpmKeyInputs } from './OpmKeyInputs';
import { useOpmInputsFromCapitalStructure } from './utils/useOpmInputsFromCapitalStructure';
import {
  CapitalStructureDto,
  CaseDto,
  DebtItemDto,
  OpmInputDto,
  PerYearValuesDto,
  ProjectDto,
} from '@app/shared/models/contracts/project-dto';
import { Navigate, generatePath } from 'react-router';
import { RouteConstants } from '../../RouteConstants';
import { selectOpmFormValues } from '@app/core/store/project-slice-selectors';
import useDeepCompareEffect from 'use-deep-compare-effect';
import {
  getInitialTotalNetDebt,
  getTotalNetDebtValues,
} from '@app/core/inputs/getOpmInputCalculatedValues';
import {
  InstrumentDefinitionAndInitialValues,
  selectCapitalStructure,
  selectCapitalStructures,
} from '@app/core/store/capital-structure-slice-selectors';
import CapitalStructureForEvents from './opm-input-table/sections/CapitalStructureForEvents';
import { AppDispatch } from '@app/core/store/store';
import { setCapitalStructureDividendsLastPwermCase } from '@app/core/store/ui-values-slice';
import { handleCaseSelectionChange as handleCaseSelectionChangeHelper } from '@app/core/inputs/handleOpmCaseSelection';
import { mergeOpmNetDebtIntoEquityBridge } from '@app/core/inputs/mergeOpmNetDebtIntoEquityBridge';

export interface OpmInputFormModel2 {
  evValue: number;
  evVolatility: number;
  perYearInputs: PerYearValuesDto[];
  forecastYears: number;
  netDebtItems: DebtItemDto[];
  slnAndPrefSharesInstruments?: InstrumentDefinitionAndInitialValues[];
  initialCashValue: number;
  valuationDateNarrative: string;
  realWorldRate: Nullable<number>;
  controlPremiumForDloc: number;
  controlPremiumForDlocByInstrument: Dictionary<number | null | undefined>;
  initialTotalNetDebt: number;
  marketVolatility: Nullable<number>;
  peerSetVolatility: Nullable<number>;
  selectedEventSetId: Nullable<string>;
  selectedCaseId: Nullable<string>;
  netDebtPerEvent: Record<string, Nullable<number>>;
}

type UpdatedOpmInput = OpmInputFormModel2 & Omit<OpmInputDto, keyof OpmInputFormModel2>;

export const OpmInputsForm2 = () => {
  const dispatch = useAppDispatch();
  const project = useAppSelector((state) => state.project.projectDraft);
  const capitalStructure = useAppSelector(selectCapitalStructure);
  const capitalStructures = useAppSelector(selectCapitalStructures);
  const capitalStructureKey = Object.keys(capitalStructures)[0];
  const savedProject = useAppSelector((state) => state.project.project);
  const isReadOnly = useIsReadOnly();
  const {
    getAllOpmValuesFromPwerm,
    getControlPremiumForDlocByInstrumentFromPwerm,
    getInitialTotalNetDebtFromPwerm: getInitialTotalNetDebtFromPwermValue,
  } = useOpmInputsFromPwerm();
  const { getEvOrEquityValueFromCapitalStructure } = useOpmInputsFromCapitalStructure();
  const formValues = useAppSelector(selectOpmFormValues);
  const [forecastYearsChangeInProgress, setForecastYearsChangeInProgress] = useState(false);
  const perYearInputs = formValues.perYearInputs;
  const forecastDates = perYearInputs.map((yi) => yi.forecastDate);
  const isOpmOnlyProject = project.details.calcMethod === CalcMethod.OPM;
  const isEquitySimulation =
    project.opmInput.opmSimulatedValue ===
    enumKeyByValue(OpmSimulatedValue, OpmSimulatedValue.Equity);

  const perYearValuesFromPwerm = useMemo(
    () => getAllOpmValuesFromPwerm(forecastDates),
    [forecastDates, getAllOpmValuesFromPwerm]
  );

  const perYearCalculatedValuesFromPwerm = useMemo(
    () =>
      isOpmOnlyProject
        ? getTotalNetDebtValues(project, forecastDates)
        : perYearValuesFromPwerm.map((values) => values.totalNetDebt),
    [forecastDates, isOpmOnlyProject, perYearValuesFromPwerm, project]
  );

  const evOrEquityValueFromCapitalStructure = useMemo(
    () => getEvOrEquityValueFromCapitalStructure(),
    [getEvOrEquityValueFromCapitalStructure]
  );

  const controlPremiumForDlocByInstrument = useMemo(
    () => getControlPremiumForDlocByInstrumentFromPwerm(),
    [getControlPremiumForDlocByInstrumentFromPwerm]
  );

  const initialTotalNetDebtFromPwerm = useMemo(
    () =>
      isOpmOnlyProject ? getInitialTotalNetDebt(project) : getInitialTotalNetDebtFromPwermValue(),
    [getInitialTotalNetDebtFromPwermValue, isOpmOnlyProject, project]
  );

  const formMethods = useForm<OpmInputFormModel2>({
    ...formConfigBase,
    defaultValues: formValues,
  });

  const { handleSubmit, getValues, reset, trigger } = formMethods;

  const notifyForecastYearsChangeInProgress = (changeInProgress: boolean) => {
    setForecastYearsChangeInProgress(changeInProgress);
  };

  const caseWeightedSetId = DividendSetId.CASE_WEIGHTED_DIVIDEND_SET_ID;
  const caseWeightedCaseId = StaticCaseSelectionOptions.CaseWeighted;
  const opmOnlySetId = DividendSetId.OPM_ONLY_DIVIDEND_SET_ID;
  const opmOnlyCaseId = StaticCaseSelectionOptions.OpmOnly;

  const updateCaseSelection = useCallback(
    (opmInput: UpdatedOpmInput, pwermCases: CaseDto[]): void => {
      if (opmInput.selectedCaseId === caseWeightedCaseId) {
        opmInput.selectedDividendSetId = caseWeightedSetId;
      } else if (opmInput.selectedCaseId === opmOnlyCaseId) {
        opmInput.selectedDividendSetId = opmOnlySetId;
      } else {
        const pwermCase = pwermCases.find(
          (caseItem) => caseItem.caseId === opmInput.selectedCaseId
        );
        if (pwermCase) {
          const eventSetIdForNoSelectedCase = isOpmOnlyProject
            ? EventKey.OpmOnly
            : EventKey.EmptyEventSet;
          opmInput.selectedEventSetId = pwermCase.eventSetId ?? eventSetIdForNoSelectedCase;
          opmInput.selectedDividendSetId = pwermCase.dividendSetId ?? null;
          if (pwermCase.dividendSetId) {
            dispatch(setCapitalStructureDividendsLastPwermCase(pwermCase.dividendSetId));
          }
        }
      }
    },
    [caseWeightedCaseId, opmOnlyCaseId, caseWeightedSetId, opmOnlySetId, isOpmOnlyProject, dispatch]
  );

  const updateCapitalStructure = useCallback(
    async (
      updatedOpmInput: UpdatedOpmInput,
      isOpmOnlyProject: boolean,
      capitalStructure: CapitalStructureDto,
      capitalStructures: Record<string, CapitalStructureDto>,
      capitalStructureKey: string,
      dispatch: AppDispatch,
      project: ProjectDto,
      isReadOnly: boolean
    ) => {
      let forecastDividendSets = capitalStructure.forecastDividendSets;

      if (isOpmOnlyProject && updatedOpmInput.selectedDividendSetId === opmOnlySetId) {
        forecastDividendSets = {
          ...forecastDividendSets,
          [opmOnlySetId]: forecastDividendSets[opmOnlySetId],
        };
      }

      if (updatedOpmInput.selectedDividendSetId === caseWeightedSetId) {
        forecastDividendSets = {
          ...forecastDividendSets,
          [caseWeightedSetId]: {
            dividendSetId: caseWeightedSetId,
            instruments: {},
          },
        };
      }
      delete updatedOpmInput.slnAndPrefSharesInstruments;
      if (!isReadOnly) {
        const updatedCapitalStructures = {
          ...capitalStructures,
          [capitalStructureKey]: {
            ...capitalStructures[capitalStructureKey],
            forecastDividendSets,
            initialValues: {
              ...capitalStructures[capitalStructureKey].initialValues,
            },
          },
        };
        await dispatch(
          projectActions.updateProjectDraft({
            project: {
              ...project,
              opmInput: { ...cloneToMutableData(updatedOpmInput) },
              capitalStructures: updatedCapitalStructures,
            },
          })
        ).unwrap();
      }
    },
    [caseWeightedSetId, opmOnlySetId]
  );

  const submitData = useCallback(async () => {
    // don't update the project draft if the number of forecast years is updating,
    // as this is handled separately in OpmKeyInputs
    if (!forecastYearsChangeInProgress) {
      const { ...updatedOpmInput } = {
        ...project.opmInput,
        ...getValues(),
      };

      const initialTotalNetDebt = isOpmOnlyProject
        ? savedProject.opmInput.initialTotalNetDebt
        : initialTotalNetDebtFromPwerm;

      // check if the EV or Equity spot value has been overridden
      updatedOpmInput.evValueSource =
        updatedOpmInput.evValue !== evOrEquityValueFromCapitalStructure
          ? OpmInputDataSource.Override
          : OpmInputDataSource.FromCapitalStructure;

      // check if the controlPremiumForDlocByInstrument values have been overridden
      if (updatedOpmInput.controlPremiumForDlocByInstrument) {
        updatedOpmInput.controlPremiumForDlocByInstrumentSource = {};
        Object.keys(updatedOpmInput.controlPremiumForDlocByInstrument).forEach((instrumentId) => {
          const currentValue = updatedOpmInput.controlPremiumForDlocByInstrument?.[instrumentId];
          const pwermValue = controlPremiumForDlocByInstrument[instrumentId];

          updatedOpmInput.controlPremiumForDlocByInstrumentSource![instrumentId] =
            currentValue !== null && currentValue !== pwermValue
              ? OpmInputDataSource.Override
              : OpmInputDataSource.FromPWERM;
        });
      }

      updatedOpmInput.forecastYearsSource = OpmInputDataSource.Override;

      // check if the initialTotalNetDebt value has been overridden
      updatedOpmInput.initialTotalNetDebtSource =
        updatedOpmInput.initialTotalNetDebt !== initialTotalNetDebt
          ? OpmInputDataSource.Override
          : OpmInputDataSource.FromPWERM;

      updateCaseSelection(updatedOpmInput, project.pwermInput.cases);

      // check to see if any of the inputs have been overridden from the value
      // suggested from PWERM data
      for (let year = 0; year < updatedOpmInput.forecastYears; year++) {
        if (
          (perYearValuesFromPwerm.length > year && perYearValuesFromPwerm[year]) ||
          isOpmOnlyProject
        ) {
          const pwermValues = perYearValuesFromPwerm[year];
          const modelValues = updatedOpmInput.perYearInputs[year];
          const initialYearTotalNetDebt = isOpmOnlyProject
            ? savedProject.opmInput.perYearInputs[year]?.totalNetDebt
            : perYearCalculatedValuesFromPwerm?.[year];

          if (isOpmOnlyProject) {
            modelValues.totalNetDebt =
              Number(modelValues.cashValue) +
              modelValues?.netDebtValues?.reduce((sum, netDebt) => (sum += netDebt.value ?? 0), 0);
          }

          modelValues.ipoProbabilitySource =
            modelValues.ipoProbability !== pwermValues.ipoProbability
              ? OpmInputDataSource.Override
              : OpmInputDataSource.FromPWERM;

          modelValues.hypotheticalDebtWhenIpoSource =
            modelValues.hypotheticalDebtWhenIpo !== pwermValues.hypotheticalDebtWhenIpo
              ? OpmInputDataSource.Override
              : OpmInputDataSource.FromPWERM;

          modelValues.ipoCostsOfEvSource =
            !isEquitySimulation && modelValues.ipoCostsOfEv !== pwermValues.ipoCostsOfEv
              ? OpmInputDataSource.Override
              : OpmInputDataSource.FromPWERM;

          modelValues.secondarySaleCostsOfEvSource =
            !isEquitySimulation &&
            modelValues.secondarySaleCostsOfEv !== pwermValues.secondarySaleCostsOfEv
              ? OpmInputDataSource.Override
              : OpmInputDataSource.FromPWERM;

          modelValues.ipoDiscountSource =
            modelValues.ipoDiscount !== pwermValues.ipoDiscount
              ? OpmInputDataSource.Override
              : OpmInputDataSource.FromPWERM;

          modelValues.equitySoldInIpoSource =
            modelValues.equitySoldInIpo !== pwermValues.equitySoldInIpo
              ? OpmInputDataSource.Override
              : OpmInputDataSource.FromPWERM;

          modelValues.postIpoSaleDlomSource =
            modelValues.postIpoSaleDlom !== pwermValues.postIpoSaleDlom
              ? OpmInputDataSource.Override
              : OpmInputDataSource.FromPWERM;

          modelValues.financingCostsSource =
            modelValues.financingCosts !== pwermValues.financingCosts
              ? OpmInputDataSource.Override
              : OpmInputDataSource.FromPWERM;

          modelValues.marketValueDloms.forEach((mvd) => {
            const pwermInstrument = pwermValues?.marketValueDloms?.find(
              (pv) => pv.instrumentId === mvd.instrumentId
            );
            mvd.instrumentDlomSource =
              mvd.instrumentDlom !== null && mvd.instrumentDlom !== pwermInstrument?.instrumentDlom
                ? OpmInputDataSource.Override
                : OpmInputDataSource.FromPWERM;
            mvd.trancheDloms.forEach((tr) => {
              const pwermTranche = pwermInstrument?.trancheDloms.find(
                (td) => td.trancheId === tr.trancheId
              );
              tr.dlomSource =
                tr.dlom !== null && tr.dlom !== pwermTranche?.dlom
                  ? OpmInputDataSource.Override
                  : OpmInputDataSource.FromPWERM;
            });
          });

          modelValues.totalNetDebtSource =
            modelValues.totalNetDebt !== initialYearTotalNetDebt
              ? OpmInputDataSource.Override
              : OpmInputDataSource.FromPWERM;

          modelValues.operationalFreeCashFlowSource =
            modelValues.operationalFreeCashFlow !== pwermValues.operationalFreeCashFlow
              ? OpmInputDataSource.Override
              : OpmInputDataSource.FromPWERM;

          modelValues.operationalEvSource =
            modelValues.operationalEv !== pwermValues.operationalEv
              ? OpmInputDataSource.Override
              : OpmInputDataSource.FromPWERM;
        }
      }

      let projectToUpdate = project;
      const submittedNetDebtItems = updatedOpmInput.netDebtItems ?? [];
      const submittedNetDebtItemIds = new Set(
        submittedNetDebtItems.map((item) => item.netDebtItemId)
      );
      const deletedNetDebtItemIds = (project.opmInput.netDebtItems ?? [])
        .filter((item) => !submittedNetDebtItemIds.has(item.netDebtItemId))
        .map((item) => item.netDebtItemId);
      if (isOpmOnlyProject) {
        const netDebtItems = updatedOpmInput.netDebtItems ?? [];
        projectToUpdate = {
          ...project,
          equityBridge: mergeOpmNetDebtIntoEquityBridge(
            project.equityBridge,
            netDebtItems,
            netDebtItems.map((item) => item.netDebtItemId),
            deletedNetDebtItemIds,
            updatedOpmInput.initialCashValue
          ),
        };
        updatedOpmInput.initialTotalNetDebt = getInitialTotalNetDebt(projectToUpdate);
      }

      await updateCapitalStructure(
        updatedOpmInput,
        isOpmOnlyProject,
        capitalStructure,
        capitalStructures,
        capitalStructureKey,
        dispatch,
        projectToUpdate,
        isReadOnly
      );
    }
  }, [
    forecastYearsChangeInProgress,
    project,
    getValues,
    isOpmOnlyProject,
    savedProject.opmInput.initialTotalNetDebt,
    savedProject.opmInput.perYearInputs,
    initialTotalNetDebtFromPwerm,
    evOrEquityValueFromCapitalStructure,
    controlPremiumForDlocByInstrument,
    isReadOnly,
    perYearValuesFromPwerm,
    perYearCalculatedValuesFromPwerm,
    isEquitySimulation,
    capitalStructure,
    dispatch,
    capitalStructures,
    capitalStructureKey,
    updateCaseSelection,
    updateCapitalStructure,
  ]);

  useDeepCompareEffect(() => {
    // update the form with any changes to the project draft
    // trigger validation after the page has rendered, otherwise
    // it does not display the results correctly
    // do a deep comparison because everytime the inputs are updated,
    // a new object is generated (even if none of the underlying values change)
    reset(formValues);
    setTimeout(() => {
      trigger();
    }, 0);
  }, [formValues]);

  const handleCaseSelectionChange = useCallback(
    async (newCaseId: string) => {
      const currentValues = getValues();
      const updatedOpmInput = await handleCaseSelectionChangeHelper({
        newCaseId,
        project,
        currentFormValues: currentValues,
        getAllOpmValuesFromPwerm,
        dispatch,
      });

      if (!isReadOnly) {
        await dispatch(
          projectActions.updateProjectDraft({
            project: {
              ...project,
              opmInput: { ...cloneToMutableData(updatedOpmInput) },
            },
          })
        ).unwrap();
      }
    },
    [project, getValues, getAllOpmValuesFromPwerm, isReadOnly, dispatch]
  );

  if (project.details.calcMethod === CalcMethod.PWERM) {
    const newPath = generatePath(`../${RouteConstants.ProjectDetails}`);
    return <Navigate to={newPath} />;
  }

  return (
    <>
      <FormProvider {...formMethods}>
        <form onBlur={handleSubmit(submitData, submitData)}>
          <OpmKeyInputs
            submitData={submitData}
            notifyForecastYearsChangeInProgress={notifyForecastYearsChangeInProgress}
            formValues={formValues}
            onCaseSelectionChange={handleCaseSelectionChange}
          />
          <CapitalStructureForEvents />
          <OpmInputTable
            submitData={submitData}
            perYearCalculatedValuesFromPwerm={perYearCalculatedValuesFromPwerm}
            initialTotalNetDebtFromPwerm={initialTotalNetDebtFromPwerm}
          />
        </form>
      </FormProvider>
    </>
  );
};
